/**
 * Модалка «Новое правило» / «Изменить правило» вкладки «Правила» (ecology-survey, макет панели
 * администратора 16.09). Правило: вопрос и ответ → документ или отчётность и действие («нужен»,
 * «есть, дата из уточнения», «требует актуализации», «пометка экологу») + пояснение клиенту и условие:
 * без условия, есть источники выбросов, категория площадки из списка, ответ на другой вопрос (или
 * вариант его уточнения) равен / не равен значению.
 *
 * В документы — только позиции, которые ведутся в пульте; обучение по экологии не добавляется
 * (оно ведётся по журналу обучения). Сервер проверяет правило авторитетно.
 */
import { useEffect, useId, useMemo, useState, type FormEvent } from 'react'
import {
  adminApi,
  type EcoCatalogItem,
  type EcoQuestion,
  type EcoRule,
  type EcoRuleAction,
  type EcoRuleCondition,
  type EcoRuleInput,
} from '@/lib/api'
import { getErrorMessage } from '@/lib/errors'
import {
  ECO_CATEGORIES,
  ECO_CATEGORY_LABELS,
  ECO_RULE_ACTION_LABELS,
  ecoAnswerValues,
  toggleCanonical,
} from '@/lib/ecology'
import { LabeledCheckbox } from '@/sout/labeled-checkbox'
import { Dialog, DialogContent } from '@/design-system/components/primitives/dialog'
import { Card } from '@/design-system/components/layout/card'
import { Button } from '@/design-system/components/primitives/button'
import { Field } from '@/design-system/components/primitives/field'
import { Alert } from '@/design-system/components/feedback/alert'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/design-system/components/primitives/select'

const TEXTAREA_CLASS =
  'w-full rounded-8 border border-line-strong bg-white px-3 py-2 text-14 text-txt outline-none transition-colors placeholder:text-txt-tertiary hover:border-line-strong focus-visible:ring-2 focus-visible:ring-accent'

/** Значения-заглушки Radix Select (пустое значение пункта недопустимо). */
const NO_ITEM = 'none'
const NO_CONDITION = 'none'
const MAIN_ANSWER = 'answer'

const ACTIONS = Object.keys(ECO_RULE_ACTION_LABELS) as EcoRuleAction[]

const CONDITIONS: { value: Exclude<EcoRuleCondition, ''> | typeof NO_CONDITION; label: string }[] =
  [
    { value: NO_CONDITION, label: 'без условия' },
    { value: 'emissions', label: 'есть источники выбросов' },
    { value: 'categories', label: 'категория площадки' },
    { value: 'answer', label: 'ответ на вопрос' },
  ]

const EMPTY: EcoRuleInput = {
  questionId: '',
  answer: '',
  catalogItemId: null,
  action: 'need',
  explanation: '',
  condition: '',
  conditionCategories: [],
  conditionQuestionId: null,
  conditionKey: '',
  conditionNegate: false,
  conditionValue: '',
}

function inputFrom(rule: EcoRule | null): EcoRuleInput {
  if (!rule) return EMPTY
  return {
    questionId: rule.questionId,
    answer: rule.answer,
    catalogItemId: rule.catalogItemId,
    action: rule.action,
    explanation: rule.explanation,
    condition: rule.condition,
    conditionCategories: rule.conditionCategories,
    conditionQuestionId: rule.conditionQuestionId,
    conditionKey: rule.conditionKey,
    conditionNegate: rule.conditionNegate,
    conditionValue: rule.conditionValue,
  }
}

interface Errors {
  questionId?: string
  answer?: string
  catalogItemId?: string
  explanation?: string
  condition?: string
}

function validate(r: EcoRuleInput): Errors {
  const errors: Errors = {}
  if (!r.questionId) errors.questionId = 'Выберите вопрос'
  else if (!r.answer) errors.answer = 'Выберите ответ'
  if (r.action !== 'note' && !r.catalogItemId)
    errors.catalogItemId = 'Выберите документ или отчётность'
  if (r.action === 'note' && !r.explanation.trim()) errors.explanation = 'Введите текст пометки'
  if (r.condition === 'categories' && r.conditionCategories.length === 0) {
    errors.condition = 'Отметьте категории'
  }
  if (r.condition === 'answer' && (!r.conditionQuestionId || !r.conditionValue)) {
    errors.condition = 'Выберите вопрос и значение условия'
  }
  return errors
}

/** Тело запроса: поля, не относящиеся к действию и условию, уходят пустыми. */
function toBody(r: EcoRuleInput): EcoRuleInput {
  const answerCond = r.condition === 'answer'
  return {
    ...r,
    explanation: r.action === 'have' ? '' : r.explanation.trim(),
    conditionCategories: r.condition === 'categories' ? r.conditionCategories : [],
    conditionQuestionId: answerCond ? r.conditionQuestionId : null,
    conditionKey: answerCond ? r.conditionKey : '',
    conditionNegate: answerCond ? r.conditionNegate : false,
    conditionValue: answerCond ? r.conditionValue : '',
  }
}

export function RuleDialog({
  open,
  rule,
  questions,
  catalog,
  onClose,
  onSaved,
}: {
  open: boolean
  /** Правка правила; null — новое. */
  rule: EcoRule | null
  questions: EcoQuestion[]
  catalog: EcoCatalogItem[]
  onClose: () => void
  onSaved: (rule: EcoRule) => void
}) {
  const formId = useId()
  const [input, setInput] = useState<EcoRuleInput>(() => inputFrom(rule))
  const [showErrors, setShowErrors] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setInput(inputFrom(rule))
    setShowErrors(false)
    setError(null)
  }, [open, rule])

  const patch = (next: Partial<EcoRuleInput>) => setInput((r) => ({ ...r, ...next }))
  const errors = showErrors ? validate(input) : {}
  const title = rule ? 'Изменить правило' : 'Новое правило'

  // Правило — только на вопрос с вариантами ответа.
  const answerable = useMemo(
    () => questions.filter((q) => ecoAnswerValues(q).length > 0),
    [questions],
  )
  const question = answerable.find((q) => q.id === input.questionId)
  const items = useMemo(() => catalog.filter((c) => c.tracked && !c.trainingLinked), [catalog])
  const condQuestion = answerable.find((q) => q.id === input.conditionQuestionId)
  const condClarifications = condQuestion?.clarifications.filter((c) => c.type === 'single') ?? []
  const condClarification = condClarifications.find((c) => c.key === input.conditionKey)
  const condValues = input.conditionKey
    ? (condClarification?.options ?? [])
    : condQuestion
      ? ecoAnswerValues(condQuestion)
      : []

  async function submit(e: FormEvent) {
    e.preventDefault()
    if (saving) return
    if (Object.keys(validate(input)).length > 0) {
      setShowErrors(true)
      return
    }
    setError(null)
    setSaving(true)
    try {
      const body = toBody(input)
      const saved = rule
        ? await adminApi.ecology.updateRule(rule.id, body)
        : await adminApi.ecology.createRule(body)
      onSaved(saved)
    } catch (err) {
      setError(getErrorMessage(err, 'Не удалось сохранить правило'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !saving) onClose()
      }}
    >
      <DialogContent title={title} className="max-w-2xl">
        <Card
          scrollable
          className="max-h-dialog"
          header={title}
          footer={
            <div className="flex flex-wrap justify-end gap-2">
              <Button variant="secondary" onClick={onClose} disabled={saving}>
                Отмена
              </Button>
              <Button type="submit" form={formId} disabled={saving}>
                {saving ? 'Сохранение…' : 'Сохранить'}
              </Button>
            </div>
          }
        >
          <form id={formId} onSubmit={submit} noValidate>
            {error ? (
              <Alert variant="danger" className="mb-3">
                {error}
              </Alert>
            ) : null}

            <Field label="Вопрос" required error={errors.questionId}>
              {(props) => (
                <Select
                  value={input.questionId || undefined}
                  onValueChange={(v) => patch({ questionId: v, answer: '' })}
                >
                  <SelectTrigger
                    id={props.id}
                    aria-label="Вопрос"
                    className="text-left"
                    invalid={Boolean(errors.questionId)}
                  >
                    <SelectValue placeholder="Выберите вопрос" />
                  </SelectTrigger>
                  <SelectContent>
                    {answerable.map((q) => (
                      <SelectItem key={q.id} value={q.id}>
                        {q.text}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </Field>

            <div className="grid grid-cols-1 gap-x-4 nw:grid-cols-2">
              <Field label="Ответ" required error={errors.answer}>
                {(props) => (
                  <Select
                    value={input.answer || undefined}
                    disabled={!question}
                    onValueChange={(v) => patch({ answer: v })}
                  >
                    <SelectTrigger
                      id={props.id}
                      aria-label="Ответ"
                      className="text-left"
                      invalid={Boolean(errors.answer)}
                    >
                      <SelectValue placeholder="Выберите ответ" />
                    </SelectTrigger>
                    <SelectContent>
                      {(question ? ecoAnswerValues(question) : []).map((v) => (
                        <SelectItem key={v} value={v}>
                          {v}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </Field>
              <Field label="Что делать">
                {(props) => (
                  <Select
                    value={input.action}
                    onValueChange={(v) => patch({ action: v as EcoRuleAction })}
                  >
                    <SelectTrigger id={props.id} aria-label="Что делать" className="text-left">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ACTIONS.map((a) => (
                        <SelectItem key={a} value={a}>
                          {ECO_RULE_ACTION_LABELS[a]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </Field>
            </div>

            <Field
              label="Документ или отчётность"
              required={input.action !== 'note'}
              hint={input.action === 'note' ? 'Необязательно для пометки' : undefined}
              error={errors.catalogItemId}
            >
              {(props) => (
                <Select
                  value={input.catalogItemId ?? NO_ITEM}
                  onValueChange={(v) => patch({ catalogItemId: v === NO_ITEM ? null : v })}
                >
                  <SelectTrigger
                    id={props.id}
                    aria-label="Документ или отчётность"
                    className="text-left"
                    invalid={Boolean(errors.catalogItemId)}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_ITEM}>не выбрано</SelectItem>
                    {items.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </Field>

            {input.action !== 'have' ? (
              <Field
                label={input.action === 'note' ? 'Текст пометки' : 'Пояснение клиенту'}
                required={input.action === 'note'}
                hint={
                  input.action === 'note'
                    ? 'Видят клиент и менеджер на экране результата опроса'
                    : 'Показывается на экране результата опроса, например: «есть источники выбросов, инвентаризация не проводилась»'
                }
                error={errors.explanation}
              >
                {(props) => (
                  <textarea
                    {...props}
                    rows={2}
                    className={TEXTAREA_CLASS}
                    value={input.explanation}
                    onChange={(e) => patch({ explanation: e.target.value })}
                  />
                )}
              </Field>
            ) : (
              <p className="my-3 text-12 text-txt-tertiary">
                Дата берётся из первого уточнения-даты вопроса; срок действия документа кабинет
                рассчитает сам.
              </p>
            )}

            <Field label="Условие" error={errors.condition}>
              {(props) => (
                <Select
                  value={input.condition || NO_CONDITION}
                  onValueChange={(v) =>
                    patch({ condition: (v === NO_CONDITION ? '' : v) as EcoRuleCondition })
                  }
                >
                  <SelectTrigger id={props.id} aria-label="Условие" className="text-left">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CONDITIONS.map((c) => (
                      <SelectItem key={c.value} value={c.value}>
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </Field>

            {input.condition === 'emissions' ? (
              <p className="-mt-2 mb-3 text-12 text-txt-tertiary">
                Отмечен хотя бы один источник выбросов, собственное теплоснабжение или транспорт на
                балансе.
              </p>
            ) : null}

            {input.condition === 'categories' ? (
              <div className="-mt-1 mb-4">
                <div className="flex flex-wrap gap-x-5 gap-y-2" role="group" aria-label="Категории">
                  {ECO_CATEGORIES.map((c) => (
                    <LabeledCheckbox
                      key={c}
                      checked={input.conditionCategories.includes(c)}
                      onCheckedChange={(on) =>
                        patch({
                          conditionCategories: toggleCanonical(
                            ECO_CATEGORIES,
                            input.conditionCategories,
                            c,
                            on,
                          ),
                        })
                      }
                    >
                      {ECO_CATEGORY_LABELS[c]}
                    </LabeledCheckbox>
                  ))}
                </div>
                <p className="mt-2 text-12 text-txt-tertiary">
                  Категория площадки; если она не указана — ответ опроса «Какая категория
                  присвоена?».
                </p>
              </div>
            ) : null}

            {input.condition === 'answer' ? (
              <div className="-mt-1 mb-2 grid grid-cols-1 gap-x-4 nw:grid-cols-2">
                <Field label="Вопрос условия" className="mt-0 nw:col-span-2">
                  {(props) => (
                    <Select
                      value={input.conditionQuestionId ?? undefined}
                      onValueChange={(v) =>
                        patch({ conditionQuestionId: v, conditionKey: '', conditionValue: '' })
                      }
                    >
                      <SelectTrigger
                        id={props.id}
                        aria-label="Вопрос условия"
                        className="text-left"
                      >
                        <SelectValue placeholder="Выберите вопрос" />
                      </SelectTrigger>
                      <SelectContent>
                        {answerable.map((q) => (
                          <SelectItem key={q.id} value={q.id}>
                            {q.text}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </Field>
                {condClarifications.length > 0 ? (
                  <Field label="Что сравнивать" className="nw:col-span-2">
                    {(props) => (
                      <Select
                        value={input.conditionKey || MAIN_ANSWER}
                        onValueChange={(v) =>
                          patch({ conditionKey: v === MAIN_ANSWER ? '' : v, conditionValue: '' })
                        }
                      >
                        <SelectTrigger
                          id={props.id}
                          aria-label="Что сравнивать"
                          className="text-left"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={MAIN_ANSWER}>ответ на вопрос</SelectItem>
                          {condClarifications.map((c) => (
                            <SelectItem key={c.key} value={c.key}>
                              уточнение «{c.label}»
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </Field>
                ) : null}
                <Field label="Сравнение">
                  {(props) => (
                    <Select
                      value={input.conditionNegate ? 'ne' : 'eq'}
                      onValueChange={(v) => patch({ conditionNegate: v === 'ne' })}
                    >
                      <SelectTrigger id={props.id} aria-label="Сравнение" className="text-left">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="eq">равно</SelectItem>
                        <SelectItem value="ne">не равно</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                </Field>
                <Field label="Значение">
                  {(props) => (
                    <Select
                      value={input.conditionValue || undefined}
                      disabled={!condQuestion}
                      onValueChange={(v) => patch({ conditionValue: v })}
                    >
                      <SelectTrigger id={props.id} aria-label="Значение" className="text-left">
                        <SelectValue placeholder="Выберите значение" />
                      </SelectTrigger>
                      <SelectContent>
                        {condValues.map((v) => (
                          <SelectItem key={v} value={v}>
                            {v}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </Field>
              </div>
            ) : null}
          </form>
        </Card>
      </DialogContent>
    </Dialog>
  )
}
